/**
 * Helpers para color de marca del reporte (multi-tenant).
 *
 * Cada `ConfiguracionFidelizacion` define `color_nombre` (ej. 'emerald') y
 * `color_tono` (ej. '500'). Las páginas del reporte usan ese color en KPIs,
 * charts y badges para que cada tenant tenga su identidad visual.
 */
import tailwindColors from 'tailwindcss/colors'

export const TONOS_DISPONIBLES = ['300', '400', '500', '600', '700'] as const
export type TonoTailwind = (typeof TONOS_DISPONIBLES)[number]

export const COLORES_DISPONIBLES = [
  'slate', 'gray', 'red', 'orange', 'amber', 'yellow', 'lime', 'green',
  'emerald', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple',
  'fuchsia', 'pink', 'rose',
] as const
export type ColorTailwind = (typeof COLORES_DISPONIBLES)[number]

export interface ReporteColor {
  nombre: string
  tono: string
}

const DEFAULT: ReporteColor = { nombre: 'emerald', tono: '500' }

export function normalizeColor(input?: { color_nombre?: string; color_tono?: string } | null): ReporteColor {
  const nombre = (input?.color_nombre || DEFAULT.nombre).toLowerCase()
  const tono = input?.color_tono || DEFAULT.tono
  const validNombre = (COLORES_DISPONIBLES as readonly string[]).includes(nombre) ? nombre : DEFAULT.nombre
  const validTono = (TONOS_DISPONIBLES as readonly string[]).includes(tono) ? tono : DEFAULT.tono
  return { nombre: validNombre, tono: validTono }
}

/** Devuelve el hex de Tailwind para un color/tono. */
export function colorHex(nombre: string, tono: string | number): string {
  const palette = (tailwindColors as unknown as Record<string, Record<string, string>>)[nombre]
  if (!palette) return tailwindColors.emerald[500]
  return palette[String(tono)] ?? palette['500']
}

/** Array de hex degradado oscuro→claro para barras tipo medifarma (7 tonos). */
export function shadesForBars(nombre: string): string[] {
  const order = ['700', '600', '500', '400', '300', '200', '100']
  return order.map((t) => colorHex(nombre, t))
}

/** Clases utilitarias para KPI cards estilo medifarma (gradient suave). */
export function kpiGradientClasses(nombre: string): string {
  // Coincide con el safelist (from-/via-/to- con opacidad).
  return [
    `from-${nombre}-500/15`,
    `via-${nombre}-500/5`,
    'via-50%',
    'to-transparent',
    `dark:from-${nombre}-800/20`,
    `dark:via-${nombre}-900/10`,
    'dark:via-50%',
    'dark:to-transparent',
    'bg-gradient-to-r',
  ].join(' ')
}

/** Clases para el icono dentro del KPI card. */
export function kpiIconClasses(nombre: string, tono: string): { wrap: string; icon: string } {
  return {
    wrap: `bg-${nombre}-${tono}/10`,
    icon: `text-${nombre}-${tono}`,
  }
}

/** Clases para botones primarios. */
export function buttonPrimaryClasses(nombre: string): string {
  // tono fijo para coherencia: 700 base / 800 hover (estándar del módulo)
  return `bg-${nombre}-700 hover:bg-${nombre}-800 text-white`
}
